import { useSplashLifecycle } from "../../hooks/useSplashLifecycle";
import SpineDrawing from "./SpineDrawing";

/**
 * SplashIntro
 * One-time-per-session opening animation: spine draws itself → fades out.
 * All lifecycle logic lives in useSplashLifecycle. All markup and animation
 * styles live here. Splitting means either can be tested / restyled in isolation.
 */
export default function SplashIntro() {
  const { visible, fadingOut } = useSplashLifecycle();
  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="splash-intro"
      className="fixed inset-0 z-[999] flex items-center justify-center bg-brand-bg"
      style={{
        opacity: fadingOut ? 0 : 1,
        pointerEvents: fadingOut ? "none" : "auto",
        transition: "opacity 380ms ease-out",
      }}
    >
      <div className="flex flex-col items-center gap-4">
        <SpineDrawing />
        <div className="font-heading text-brand-blue text-lg tracking-wide opacity-80 ph-fadein">
          Physio Hyve Clinic
        </div>
      </div>

      <SplashStyles />
    </div>
  );
}

/** All keyframes + selectors kept co-located with the component that uses them. */
function SplashStyles() {
  return (
    <style>{`
      .ph-spine-svg { overflow: visible; }
      .ph-head {
        fill: #2AB6E5;
        stroke: #1E4FA8;
        stroke-width: 2.5;
        transform-origin: 60px 14px;
        animation: ph-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.35s both;
      }
      .ph-spine {
        stroke: #1F3B2B;
        stroke-width: 4.5;
        fill: none;
        stroke-dasharray: 240;
        stroke-dashoffset: 240;
        animation: ph-draw 0.85s cubic-bezier(0.65, 0, 0.35, 1) 0.05s forwards;
      }
      .ph-vert {
        stroke: #6B8E5A;
        stroke-width: 3.5;
        opacity: 0;
        animation: ph-vert-in 0.28s ease-out forwards;
      }
      ${[0,1,2,3,4,5,6].map(i => `.ph-vert-${i} { animation-delay: ${0.35 + i*0.05}s; }`).join("\n")}
      .ph-fadein {
        opacity: 0;
        animation: ph-fadein 0.5s ease-out 0.75s forwards;
      }
      @keyframes ph-draw { to { stroke-dashoffset: 0; } }
      @keyframes ph-vert-in {
        from { opacity: 0; transform: scaleX(0.4); }
        to   { opacity: 1; transform: scaleX(1); }
      }
      @keyframes ph-pop {
        from { transform: scale(0); opacity: 0; }
        to   { transform: scale(1); opacity: 1; }
      }
      @keyframes ph-fadein { to { opacity: 0.9; } }
      @media (prefers-reduced-motion: reduce) {
        .ph-spine, .ph-head, .ph-vert, .ph-fadein { animation-duration: 0.01s !important; }
      }
    `}</style>
  );
}
